/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import PropTypes from 'prop-types';

import { Box, Button, Grid } from '@material-ui/core';

import { QB_RISK_DETAILS, QB_RISK_ENDORSEMENTS, QB_RISK_QUOTES, useUpdateLayerPremiums } from 'lib/quoteBind';
import { hideModal } from 'stores';

import { LayerPremium } from './LayerPremium';

import { useStyles } from './EditLayersRiskQuote.styles';

const LAYER_DEFINITION_NAME = 'layers';

const EditLayersRiskQuote = ({ riskData, quote, product, endorsmentId }) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { risk, layerPremiumOverrideFields, layerEndorsementPremiumOverrideFields } = riskData;
  const [formErrors, setFormErrors] = useState({});
  const { mutateAsync: updateLayerPremiums, isLoading } = useUpdateLayerPremiums();

  const overrideFields = endorsmentId ? layerEndorsementPremiumOverrideFields : layerPremiumOverrideFields;
  const riskObject = endorsmentId ? quote.postPatchRisk : risk;

  const methods = useForm();

  const layerDefinition = useMemo(() => product.find((field) => field.name === LAYER_DEFINITION_NAME), []);
  const layerFieldsDefinitions = useMemo(() => layerDefinition?.arrayItemDef ?? [], []);

  // Layer Premiums is an array of objects with the following structure:
  // {
  //   name: string, // The name of the layer
  //   premiums: {
  //     [string]: number, // The premiums for the layer. Key is the field name, value is the premium value
  //   },
  // }
  // Premium values are fetched from riskObject for quotes and from postPatchRisk for endorsements
  const layerPremiums = useMemo(
    () =>
      riskObject[LAYER_DEFINITION_NAME]?.map((layer) => ({
        name: layer[layerDefinition.itemTitleField],
        premiums: {
          ...overrideFields.reduce((acc, field, index) => {
            const isActivePeril = index === 0 || (index === 1 && layer.triaIncluded);

            if (isActivePeril) {
              return {
                ...acc,
                [field]: layer[field] ?? 0,
              };
            }

            return acc;
          }, {}),
        },
      })),
    []
  );

  const resetError = (name) => {
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  };

  const handleSave = async () => {
    const values = methods.getValues();
    const errors = {};

    // Validate that all fields have a value. It can be 0
    layerPremiums.forEach((layerPremium) => {
      Object.keys(layerPremium.premiums).forEach((premium) => {
        const value = get(values, `${layerPremium.name}.${premium}`);
        if (typeof value === 'undefined' || value === null || value === '') {
          errors[`${layerPremium.name}.${premium}`] = 'Required';
        }
      });
    });

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const data = Object.keys(values).map((key) => ({
      name: key,
      premiums: values[key],
    }));

    await updateLayerPremiums({ id: risk.id, data, endorsmentId });

    queryClient.invalidateQueries(QB_RISK_DETAILS, { id: risk.id });
    queryClient.invalidateQueries(QB_RISK_QUOTES);
    if (endorsmentId) {
      queryClient.invalidateQueries(QB_RISK_ENDORSEMENTS);
    }
    dispatch(hideModal());
  };

  return (
    <FormProvider {...methods}>
      <Box className={classes.root}>
        <Grid container spacing={2}>
          {layerPremiums.length > 0 &&
            layerPremiums.map((layerPremium) => (
              <LayerPremium
                key={layerPremium.name}
                layerPremium={layerPremium}
                control={methods.control}
                watch={methods.watch}
                formErrors={formErrors}
                resetError={resetError}
                layerFieldsDefinitions={layerFieldsDefinitions}
                isEndorsement={!!endorsmentId}
              />
            ))}
          <Grid item xs={12} className={classes.actions}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={isLoading}>
              Save
            </Button>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  );
};

EditLayersRiskQuote.propTypes = {
  quote: PropTypes.shape({
    postPatchRisk: PropTypes.shape({
      layers: PropTypes.arrayOf(
        PropTypes.shape({
          [PropTypes.string]: PropTypes.number,
        })
      ),
    }),
  }),
  riskData: PropTypes.shape({
    risk: PropTypes.shape({
      id: PropTypes.string.isRequired,
      layers: PropTypes.arrayOf(
        PropTypes.shape({
          [PropTypes.string]: PropTypes.number,
        })
      ).isRequired,
    }).isRequired,
    layerPremiumOverrideFields: PropTypes.arrayOf(PropTypes.string).isRequired,
    layerEndorsementPremiumOverrideFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  product: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      arrayItemDef: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  endorsmentId: PropTypes.string,
};

export default EditLayersRiskQuote;
